/*
 * Per-language configuration for the blog templates.
 *
 * The DATA lives in data/locales.json, not here, and that split is deliberate: the .mjs scripts under
 * scripts/ (the content generator, the QA gate) cannot import TypeScript, so anything defined only in
 * this file would have to be duplicated there. One JSON manifest, two consumers, no drift.
 *
 * WHY THIS EXISTS AT ALL
 * `/de/blog/*` is ten hand-authored .astro files. Every string in them — including the byline labels —
 * was translated once, by hand, in 2023. That is how "Überprüft von" (reviewed by) came to stand in for
 * "Medically Reviewed by" on all ten pages, dropping the actual E-E-A-T signal, and why GEO audit M13
 * (no visible dates) and C3 (no server-side schema) are ten separate fixes rather than one.
 *
 * So every non-English tree shares one template and varies only through this table. A template fix
 * lands on every language at once, and the whole label set is auditable in a single file.
 *
 * ADDING A LANGUAGE is not just adding a row. It also needs: a content collection in
 * src/content/config.ts, a route under src/pages/<code>/blog/[slug].astro, and a hub page so /<code>
 * resolves. Chrome partials in src/chrome/ are optional — a locale with none renders English chrome,
 * which the SEO advisor confirmed is polish rather than a ranking issue.
 */
import manifest from '../../data/locales.json';

export interface LocaleLabels {
  published: string;
  updated: string;
  writtenBy: string;
  /*
   * TWO reviewer labels because English has two, and the English strings are frozen: production renders
   * the weaker "Reviewed by" in the hero and "Medically Reviewed by" in the editorial block, and
   * scripts/parity-blog.mjs compares both against 229 live pages.
   *
   * A new locale should NOT reproduce that split. The medical framing is the actual E-E-A-T signal, so
   * translated locales carry it in both slots.
   */
  heroReviewedBy: string;
  medicallyReviewedBy: string;
  /** Used when the reviewer profile is not `adviserReviewer` (e.g. Jeff Kahn). */
  editorialReviewedBy: string;
  editorialTeam: string;
  references: string;
  showAll: string;
  viewAll: string;
  faqsHeading: string;
  categoryTag: string;
  emptyStrip: string;
  /** Template-level YMYL disclaimer. Rendered on translated posts and native English posts only. */
  medicalDisclaimer: string;
  medicalDisclaimerLink: string;
}

export interface Locale {
  /** Language subtag for <html lang>. */
  lang: string;
  /** BCP-47 tag for JSON-LD `inLanguage`. Regionalized: es-MX and pt-BR are the targeted markets. */
  inLanguage: string;
  /** hreflang value. `pt-BR` rather than bare `pt`, because Brazil is the market and Portugal is not. */
  hreflang: string;
  /** URL prefix for this locale's blog posts, no trailing slash. */
  base: string;
  /** The locale's landing page, used as the topic strip's "view all" target. */
  hub: string;
  /** Content collection holding this locale's posts. */
  collection: 'blog' | 'blog-es' | 'blog-pt';
  labels: LocaleLabels;
  /*
   * Which src/chrome/ partial each block uses, for THIS locale. Named explicitly with no implicit
   * fallback: Chrome.astro throws on a missing variant, and a silent fallback is exactly how Spanish
   * controls ended up on Portuguese pages when the shared template hardcoded the .es variants.
   */
  chrome: { nav: string; 'sticky-top': string; footer: string; 'cookie-banner': string };
  /** Per-locale human sign-off. null = not reviewed. Surfaced by scripts/check-localization.mjs. */
  humanSignoff: { nativeReview: string; hedgeAudit: string; by: string; slugs?: string[] } | null;
}

type ManifestEntry = Locale & { overlayDir: string | null; dateMonths: string[] | null; dateTemplate: string | null };

const entries = Object.fromEntries(
  Object.entries(manifest).filter(([k]) => !k.startsWith('_')),
) as unknown as Record<string, ManifestEntry>;

export const EN = entries.en as Locale;
export const ES = entries.es as Locale;
export const PT = entries.pt as Locale;

export const LOCALES = { en: EN, es: ES, pt: PT } as const;
export type LocaleCode = keyof typeof LOCALES;

/** Every locale that is not the byte-locked English tree. Used by src/lib/localized-routes.ts. */
export const TRANSLATED_LOCALES: Locale[] = Object.values(LOCALES).filter((l) => l.collection !== 'blog');
